import store from '../store.js'
import api from '../api.js'
import { getSpAccessToken, getGraphAccessToken } from '../../lib/msal.js'
import { ERROR_ACTION } from '../error/actions.js'
import {
  beginProgressDialog,
  updateProgressDialog,
  endProgressDialog
} from '../progress/actions.js'
import {
  closeOutlookWindow,
  emailFileNameOutlookComposeMode,
  getFileExtensionFromHost,
  isOffice,
  isOutlook,
  isOutlookComposeMode,
  isWordFile,
  isExcelFile,
  readCustomXml,
  readFileAttachments,
  readOfficeFile,
  readCurrentMailFile,
  readMailFile,
  readMailMetadata,
  saveDraftEmail,
  sendMail,
  setDocumentTitle,
  setPDSCategory,
  setDocumentProperty,
  writeCustomXml,
  resetCustomXml,
  getEmailMetadataReadMode,
  setEmailSubjectComposeMode,
  getEmailSubjectComposeMode,
  writeHiddenXml,
  convertTemplateToFile
} from '../../lib/office.js'
import {
  readSharePointFile,
  writeSharePointFile,
  updateEmailListItemFields,
  getDocumentLibraryFiles,
  ensureSharePointFolder
} from '../../lib/sharepoint'
import {
  stringToUint,
  cleanFileName,
  openDocument,
  getFileExtension,
  getItemUrl,
  wait,
  isTemplateFile
} from '../../util.js'
import {
  xmlToObject,
  XML_DEFAULT_DATA
} from '../../lib/xml.js'
import config from '../../config.js'

export const SET_SAVE_PROGRESS_ACTION = 'SET_SAVE_PROGRESS_ACTION'
export const SET_INITIAL_SAVE_PROGRESS_ACTION = 'SET_INITIAL_SAVE_PROGRESS_ACTION'
export const RESET_SAVE_PROGRESS_ACTION = 'RESET_SAVE_PROGRESS_ACTION'
export const SET_SAVE_DIALOG_FILENAME_ACTION = 'SET_SAVE_DIALOG_FILENAME_ACTION'
export const READ_DOCUMENT_PROPERTIES_ACTION = 'READ_DOCUMENT_PROPERTIES_ACTION'
export const SET_DOCUMENT_DATA_ACTION = 'SET_DOCUMENT_DATA_ACTION'
export const SET_TEMPLATE_RUN_REPORT_ACTION = 'SET_TEMPLATE_RUN_REPORT_ACTION'
export const DISMISS_TEMPLATE_RUN_REPORT_ACTION = 'DISMISS_TEMPLATE_RUN_REPORT_ACTION'

const ERROR_TYPE_FILE_SAVE = 'ERROR_TYPE_FILE_SAVE'
const ERROR_TYPE_FILE_READ_ATTACHMENTS = 'ERROR_TYPE_FILE_READ_ATTACHMENTS'
const ERROR_TYPE_FILE_UPLOAD = 'ERROR_TYPE_FILE_UPLOAD'
const ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS = 'ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS'
const ERROR_TYPE_EMAIL_READ = 'ERROR_TYPE_EMAIL_READ'
const ERROR_TYPE_EMAIL_SAVE_DRAFT = 'ERROR_TYPE_EMAIL_SAVE_DRAFT'
const ERROR_TYPE_EMAIL_SEND = 'ERROR_TYPE_EMAIL_SEND'

function handleGetErrors (dataType, dispatch) {
  dispatch({
    type: ERROR_ACTION,
    errorType: ERROR_TYPE_FILE_SAVE,
    errorMessage: `failed to fetch ${dataType}`
  })
}

function normalizePersonRoleForHiddenData (item = {}) {
  const { address, ...rest } = item
  const addressObject = (address && typeof address === 'object') ? address : {}
  const addressValue = typeof address === 'string' ? address : undefined
  const personAddressObject = (item?.person_address && typeof item.person_address === 'object') ? item.person_address : {}
  const personAddressValue = typeof item?.person_address === 'string' ? item.person_address : undefined

  const pickFirstFilled = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue
      if (typeof value === 'string' && value.trim() === '') continue
      return value
    }
    return ''
  }

  const normalizedPersonName = pickFirstFilled(item?.person_name, item?.name)
  const normalizedPersonCollectionName = pickFirstFilled(item?.person_collection_name, item?.collection_name)

  return {
    ...rest,
    id: item?.id ?? item?.person_id,
    person_id: item?.person_id ?? item?.id,
    person_collection_name: normalizedPersonCollectionName === normalizedPersonName ? '' : normalizedPersonCollectionName,
    person_name: normalizedPersonName,
    role_name: pickFirstFilled(item?.role_name, item?.role?.name, item?.role),
    address: pickFirstFilled(addressValue, personAddressObject?.address, addressObject?.address, item?.street),
    person_address: pickFirstFilled(personAddressValue, personAddressObject?.address, addressObject?.address, addressValue, item?.street),
    person_postal_code: pickFirstFilled(item?.person_postal_code, personAddressObject?.postal_code, addressObject?.postal_code, item?.postal_code, item?.zip_code, item?.zip),
    person_city: pickFirstFilled(item?.person_city, personAddressObject?.city, addressObject?.city, item?.city),
    person_email: pickFirstFilled(item?.person_email, personAddressObject?.email, addressObject?.email, item?.email),
    person_phone: pickFirstFilled(item?.person_phone, personAddressObject?.phone, addressObject?.phone, item?.phone, item?.mobile),
    person_homepage: pickFirstFilled(item?.person_homepage, personAddressObject?.homepage, addressObject?.homepage, item?.homepage, item?.website, item?.url)
  }
}

function buildHiddenDocumentData (documentData = {}) {
  const contact = documentData.contact || {}
  const project = documentData.project || {}
  const selectedPersonRole = documentData.selectedPersonRole || {}
  const selectedRoleId = selectedPersonRole.id
  const selectedPersonIdFromSelection = selectedPersonRole.person_id ?? selectedRoleId

  const contactAddressRole = {
    id: 0,
    person_id: 0,
    person_collection_name: contact?.name || '',
    person_name: contact?.name || '',
    role_name: 'Klientadress',
    person_address: contact?.address?.address || '',
    person_postal_code: contact?.address?.postal_code || '',
    person_city: contact?.address?.city || '',
    person_email: contact?.address?.email || '',
    person_phone: contact?.address?.phone || '',
    person_homepage: contact?.address?.homepage || ''
  }

  const existingProjectRoles = Array.isArray(project?.person_role) ? project.person_role : []
  const normalizedProjectRoles = existingProjectRoles.map((role) => normalizePersonRoleForHiddenData(role))
  const filteredProjectRoles = normalizedProjectRoles.filter((role) => role?.id !== 0)
  const projectPersonRoles = [{
    ...contactAddressRole,
    person_collection_name: contactAddressRole.person_collection_name === contactAddressRole.person_name
      ? ''
      : contactAddressRole.person_collection_name
  }, ...filteredProjectRoles]

  const hasSelectedRoleId = selectedRoleId !== undefined && selectedRoleId !== null && selectedRoleId !== ''
  const selectedExistsByRoleId = projectPersonRoles.some((role) => String(role?.id) === String(selectedRoleId))
  const shouldAppendSelectedRole = hasSelectedRoleId && !selectedExistsByRoleId

  const selectedRoleForProject = shouldAppendSelectedRole
    ? {
        ...normalizePersonRoleForHiddenData(selectedPersonRole),
        id: selectedRoleId,
        person_id: selectedPersonIdFromSelection
      }
    : null

  const projectPersonRolesWithSelected = selectedRoleForProject ? [...projectPersonRoles, selectedRoleForProject] : projectPersonRoles

  const selectedProjectRole = projectPersonRolesWithSelected.find((role) => String(role?.id) === String(selectedRoleId)) ||
    projectPersonRolesWithSelected.find((role) => String(role?.person_id) === String(selectedPersonIdFromSelection))
  const selectedPersonId = selectedProjectRole?.person_id ??
    ((selectedRoleId === 'contact-address' || selectedRoleId === 'contactaddress') ? 0 : selectedPersonIdFromSelection)

  return {
    ...documentData,
    project: {
      ...project,
      person_role: projectPersonRolesWithSelected
    },
    selectedPersonRole: {
      id: selectedPersonId
    }
  }
}

export function setSaveDialogFileName (fileName) {
  return {
    type: SET_SAVE_DIALOG_FILENAME_ACTION,
    fileName: cleanFileName(fileName)
  }
}

/**
 * Sets a property, but only for saving the state. It doesn't save
 * this property to the document.
 */
export function setProperty (key, value) {
  return {
    type: SET_DOCUMENT_DATA_ACTION,
    key,
    value
  }
}

export function fetchProjectData (params) {
  return async function (dispatch) {
    api.getProjects({ query: params }).then(async (result) => {
      if (result.data.value && result.data.value.length > 0) {
        dispatch({
          type: SET_DOCUMENT_DATA_ACTION,
          key: 'project',
          value: result.data.value[0]
        })

        const contactParams = [{
          key: 'id',
          op: '=',
          value: result.data.value[0].collection_id
        }]
        dispatch(fetchContactData(contactParams))
      }
      // TODO: handle no project found
    }).catch((e) => {
      handleGetErrors('project', dispatch)
    })
  }
}

export function fetchContactData (params) {
  return async function (dispatch) {
    api.getContacts({ query: params }).then(result => {
      if (result.data.value && result.data.value.length > 0) {
        dispatch({
          type: SET_DOCUMENT_DATA_ACTION,
          key: 'contact',
          value: result.data.value[0]
        })
      }
      // TODO: handle no contact found
    }).catch((e) => {
      handleGetErrors('contact', dispatch)
    })
  }
}

/**
 * Async action for reading document properties.
 */
export function readDocumentProperties () {
  return async function (dispatch) {
    let xml

    try {
      xml = await readCustomXml()
      console.info('read custom xml', xml)
    } catch (e) {
      console.warn('failed to read custom xml', e)
      dispatch({
        type: READ_DOCUMENT_PROPERTIES_ACTION,
        documentProperties: {}
      })
      return
    }

    if (!xml) {
      console.info('no custom xml found, using default nil values')
      xml = XML_DEFAULT_DATA
    }
    dispatch({
      type: READ_DOCUMENT_PROPERTIES_ACTION,
      documentProperties: xmlToObject(xml)
    })
  }
}

/**
 * Async action for uploading a file to SharePoint.
 * siteItem: item picked in top tree, e.g. contact, project, intranet, department
 * fileItem: file node picked in the bottom tree
 * fileName: file name for this file
 * xmlProperties: properties to populate custom xml with
 * onSaveDone: call this function when save is done
 */
export function uploadFile (siteItem, fileItem, fileName, xmlProperties, onSaveDone) {
  const state = store.getState()
  const userData = state.login.userData
  fileName = computeFileName(fileName)

  return async function (dispatch) {
    try {
      // TODO figure out how to do this using jszip later, currently modifying
      // existing document!
      await setDocumentTitle(fileName.split('.')[0])
    } catch (e) {
      console.warn('failed to set document title', e)
    }

    const siteUrl = getItemUrl(siteItem)
    const path = fileItem.path
    let fileData = null

    try {
      dispatch(
        beginProgressDialog(
          'PROGRESS_TITLE_UPLOAD_FILE',
          'PROGRESS_STATUS_READ_FILE'
        )
      )

      const spToken = await getSpAccessToken(userData)

      if (isOffice()) {
        fileData = await readOfficeFile()
        // Read office file data into uint8array
        dispatch(updateProgressDialog('PROGRESS_STATUS_WRITE_CUSTOM_XML'))
        // Set custom xml to default xml, if it exists
        fileData = await resetCustomXml(fileData)

        // Upload file to have SharePoint populate/add custom xml
        console.info('upload: saving document for custom xml', siteUrl, path, fileName)
        const result = await writeSharePointFile({ siteUrl, path, fileName, fileData }, spToken)
        // Give SharePoint some time to process the file
        await wait(config.waitForSaveTimeout)

        // Read the file data back from SharePoint.
        fileData = await readSharePointFile(result.absoluteUrl, spToken)
        // Now we can populate the custom xml with the properties from the UI
        fileData = await writeCustomXml(fileData, xmlProperties)
      } else if (isOutlook()) {
        const graphToken = await getGraphAccessToken(userData)
        fileData = await readCurrentMailFile(graphToken)
      } else {
        console.info('not office and not outlook -> assuming browser')
        fileData = stringToUint('This is a test file')
      }

      console.info('upload: saving document', siteUrl, path, fileName)
      dispatch(updateProgressDialog('PROGRESS_STATUS_UPLOAD_FILE'))

      const result = await writeSharePointFile({
        siteUrl,
        path,
        fileName,
        fileData
      }, spToken)

      console.info('upload: file saved successfully')

      if (typeof onSaveDone === 'function') onSaveDone()

      try {
        // Signal to close the currently opened document
        await setDocumentProperty('PDSCloseDocument', 1)
      } catch (e) {
        console.warn('failed to set document property', e)
      }

      if (isOffice()) {
        dispatch(updateProgressDialog('PROGRESS_STATUS_OPEN_FILE'))
        openDocument(result.absoluteUrl)
      } else if (isOutlook()) {
        setPDSCategory()
        await updateEmailListItemFields({
          siteUrl,
          listItemUrl: result.listItemUrl,
          fields: getEmailMetadataReadMode()
        }, spToken)
      }

      const meta = {
        type: siteItem.type,
        id: siteItem.id,
        path,
        fileName,
        url: encodeURI(`${siteUrl}/${path}/${fileName}`)
      }
      try {
        await api.addFileSavedEvent(meta)
      } catch (e) {
        console.warn('failed to add file saved event', e)
      }

      setTimeout(() => dispatch(endProgressDialog()), 1000)
    } catch (e) {
      dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FILE_SAVE,
        errorMessage: e.message
      })
    }
  }
}

/**
 * Creates and uploads multiple templates to SharePoint.
 */

export function createAndUploadMultipleTemplates (templateSiteUrl, selectedTemplateFileNode, documentData, savePath, onSaveDone) {
  return async function (dispatch) {
    const source = { name: selectedTemplateFileNode.name, path: selectedTemplateFileNode.serverRelativeUrl, kind: 'source' }
    return runTemplateReport(dispatch, source, async context => {
      const { report, publish, fail, start, finish } = context
      const spToken = await runTemplateStep('reading', () => getSpAccessToken(store.getState().login.userData))
      const destinationSiteUrl = documentData.project?.url ?? documentData.contact?.url
      const upload = createTemplateUploader(destinationSiteUrl, spToken)
      const sourceRoot = source.path.replace(/\/+$/, '')
      const destinationRoot = savePath.replace(/\/+$/, '')
      const folders = []
      const folderLevels = []
      const documents = []
      const failedFolders = []
      const destinationPath = sourcePath => {
        if (!sourcePath.startsWith(`${sourceRoot}/`)) {
          throw Object.assign(new Error(`Source path is outside the selected folder: ${sourcePath}`), { stage: 'reading' })
        }
        return `${destinationRoot}/${sourcePath.slice(sourceRoot.length + 1)}`
      }
      const checkParent = path => {
        if (failedFolders.some(folder => path === folder || path.startsWith(`${folder}/`))) {
          throw Object.assign(new Error(`Destination folder is unavailable: ${path}`), { stage: 'folders', code: 'DESTINATION_FOLDER_UNAVAILABLE' })
        }
      }
      const discover = async (item, children) => {
        const active = start(item)
        try {
          const result = await runTemplateStep('reading', () => getDocumentLibraryFiles({ siteUrl: templateSiteUrl, path: item.path }, spToken))
          if (!Array.isArray(result?.Files?.results) || !Array.isArray(result?.Folders?.results) || result.Files.__next || result.Folders.__next) {
            throw Object.assign(new Error('Source folder listing is incomplete'), { stage: 'reading' })
          }
          for (const file of result.Files.results) {
            const path = destinationPath(file.ServerRelativeUrl)
            documents.push({
              name: file.Name,
              path: file.ServerRelativeUrl,
              destination: path.slice(0, path.lastIndexOf('/')),
              kind: isTemplateFile(getFileExtension(file.Name)) ? 'template' : 'file',
              contentType: file.ListItemAllFields?.pdscol_document_class
            })
          }
          for (const folder of result.Folders.results) {
            if (folder.Name === 'pdsMallar') continue
            const child = { name: folder.Name, path: folder.ServerRelativeUrl, kind: 'source' }
            folders.push({ ...child, kind: 'folder', destination: destinationPath(child.path) })
            children.push(child)
          }
        } catch (error) {
          report.sourceComplete = false
          fail(item, error)
        } finally {
          finish(active)
        }
      }

      let sources = [source]
      while (sources.length) {
        const children = []
        const offset = folders.length
        await runTemplateQueue(sources, item => discover(item, children))
        folderLevels.push(folders.slice(offset))
        sources = children
      }
      report.total = documents.length
      report.foldersTotal = folders.length
      report.phase = 'folders'
      publish()

      for (const level of folderLevels) {
        await runTemplateQueue(level, async folder => {
          const active = start(folder)
          try {
            await runTemplateStep('folders', async () => {
              checkParent(folder.destination)
              await ensureSharePointFolder({ siteUrl: destinationSiteUrl, path: folder.destination }, spToken)
            })
          } catch (error) {
            failedFolders.push(folder.destination)
            fail(folder, error)
          }
          report.foldersProcessed++
          finish(active)
        })
      }

      await runTemplateQueue(documents, async document => {
        await runTemplateDocument(context, document, async () => {
          checkParent(document.destination)
          const uri = new URL(document.path, templateSiteUrl).href
          if (document.kind === 'template') {
            return prepareAndUploadTemplate(uri, document.name, documentData, document.contentType, document.destination, spToken, upload)
          }
          const fileData = await runTemplateStep('reading', () => readSharePointFile(uri, spToken))
          return runTemplateStep('uploading', () => upload(document.destination, document.name, fileData))
        }, document.kind === 'template' ? onSaveDone : undefined)
      }, 5)
    })
  }
}

/**
 * Adding custom xml to a template and uploading it to SharePoint.
 */
export function createAndUploadTemplate (templateUri, fileName, documentData, contentType, savePath, onSaveDone) {
  return async function (dispatch) {
    const item = { name: fileName, path: templateUri, kind: 'template' }
    return runTemplateReport(dispatch, item, context => runTemplateDocument(context, item, async () => {
      const spToken = await runTemplateStep('reading', () => getSpAccessToken(store.getState().login.userData))
      return prepareAndUploadTemplate(templateUri, fileName, documentData, contentType, savePath, spToken)
    }, onSaveDone))
  }
}

async function runTemplateStep (stage, operation) {
  try {
    return await operation()
  } catch (cause) {
    const error = new Error(cause?.message || String(cause || 'Unknown error'))
    error.stage = cause?.stage || stage
    error.code = cause?.code || null
    error.status = cause?.status || null
    throw error
  }
}

async function runTemplateReport (dispatch, source, operation) {
  if (store.getState().save.templateRun?.running) return

  const report = {
    running: true,
    isBatch: source.kind === 'source',
    phase: 'reading',
    total: source.kind === 'template' ? 1 : 0,
    processed: 0,
    succeeded: 0,
    foldersTotal: 0,
    foldersProcessed: 0,
    sourceComplete: true,
    currentName: source.name,
    currentPath: source.path,
    activeItems: [],
    failures: []
  }
  const publish = () => dispatch({
    type: SET_TEMPLATE_RUN_REPORT_ACTION,
    report: { ...report, activeItems: [...report.activeItems], failures: [...report.failures] }
  })
  const start = item => {
    const active = { name: item.name, path: item.kind === 'folder' ? item.destination : item.path }
    report.activeItems.push(active)
    report.currentName = report.activeItems[0].name
    report.currentPath = report.activeItems[0].path
    publish()
    return active
  }
  const finish = active => {
    report.activeItems = report.activeItems.filter(item => item !== active)
    report.currentName = report.activeItems[0]?.name || ''
    report.currentPath = report.activeItems[0]?.path || ''
    publish()
  }
  const fail = (item, error) => {
    report.failures.push({
      ...item,
      stage: error?.stage || 'processing',
      message: error?.message || String(error || 'Unknown error'),
      code: error?.code || null,
      status: error?.status || null
    })
    publish()
  }

  publish()
  try {
    await operation({ report, publish, fail, start, finish })
  } catch (error) {
    report.sourceComplete = false
    fail(source, error)
  } finally {
    report.running = false
    report.currentName = ''
    report.currentPath = ''
    publish()
  }
  return report
}

async function runTemplateDocument (context, item, operation, onSaveDone) {
  const { report, publish, fail, start, finish } = context
  report.phase = 'documents'
  const active = start(item)
  let result
  try {
    result = await operation()
    report.succeeded++
  } catch (error) {
    fail(item, error)
  }
  report.processed++
  publish()

  if (result && onSaveDone) {
    try {
      await runTemplateStep('opening', () => onSaveDone(result.absoluteUrl))
    } catch (error) {
      fail({ ...item, kind: 'opening' }, error)
    }
  }
  finish(active)
}

export function dismissTemplateRunReport () {
  return { type: DISMISS_TEMPLATE_RUN_REPORT_ACTION }
}

async function runTemplateQueue (items, operation, concurrency = 3) {
  let next = 0
  const results = await Promise.allSettled(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++]
      await operation(item)
    }
  }))
  const failure = results.find(result => result.status === 'rejected')
  if (failure) throw failure.reason
}

function createTemplateUploader (siteUrl, spToken) {
  const destinations = new Map()
  return async (path, fileName, fileData) => {
    const key = path.toLowerCase()
    if (!destinations.has(key)) {
      destinations.set(key, (async () => {
        const existing = await getDocumentLibraryFiles({ siteUrl, path }, spToken)
        if (!Array.isArray(existing?.Files?.results) || existing.Files.__next) throw new Error('Destination file listing is incomplete')
        return new Set(existing.Files.results.map(file => file.Name.toLowerCase()))
      })())
    }
    const names = await destinations.get(key)
    const separator = fileName.lastIndexOf('.')
    const baseName = separator > 0 ? fileName.slice(0, separator) : fileName
    const extension = separator > 0 ? fileName.slice(separator) : ''
    let counter = 1
    for (let attempt = 0; attempt < 3; attempt++) {
      let uniqueName = fileName
      while (names.has(uniqueName.toLowerCase())) uniqueName = `${baseName} (${counter++})${extension}`
      names.add(uniqueName.toLowerCase())
      try {
        return await writeSharePointFile({ siteUrl, path, fileName: uniqueName, fileData, overwrite: false }, spToken)
      } catch (error) {
        if (error.status !== 409 || attempt === 2) throw error
      }
    }
  }
}

async function prepareAndUploadTemplate (templateUri, fileName, documentData, contentType, savePath, spToken, upload) {
  let fileData = await runTemplateStep('reading', () => readSharePointFile(templateUri, spToken))
  fileData = await runTemplateStep('processing', async () => {
    if (contentType) {
      fileData = await writeCustomXml(fileData, { pdscol_document_class: contentType })
    }
    fileData = await writeHiddenXml(fileData, JSON.stringify(buildHiddenDocumentData(documentData)))
    const sourceExtension = getFileExtension(new URL(templateUri).pathname)
    const outputExtensions = { dotx: 'docx', dotm: 'docm', docx: 'docx', xltx: 'xlsx', xltm: 'xlsm', xlsx: 'xlsx', xlsm: 'xlsm' }
    const extension = outputExtensions[sourceExtension]
    if (!extension || (getFileExtension(fileName) && !isWordFile(fileName) && !isExcelFile(fileName) && getFileExtension(fileName) !== 'docm')) {
      throw Object.assign(new Error('Invalid file extension'), { code: 'INVALID_DOCUMENT_EXTENSION' })
    }
    fileName = getFileExtension(fileName) ? `${fileName.slice(0, fileName.lastIndexOf('.'))}.${extension}` : `${fileName}.${extension}`
    const converted = await convertTemplateToFile(templateUri, fileData)
    if (!converted?.length) throw new Error('Template conversion returned no document data')
    return converted
  })

  const siteUrl = documentData.project?.url ?? documentData.contact?.url
  return runTemplateStep('uploading', () => (upload || createTemplateUploader(siteUrl, spToken))(savePath, fileName, fileData))
}

/**
 * Called from read mode in outlook when saving attachments.
 * siteItem: item picked in top tree, e.g. contact, project, intranet, department
 * fileItem: file node picked in the bottom tree
 * onSaveDone: call this function when save is done
 */
export function uploadFileAttachments (siteItem, fileItem, onSaveDone) {
  const state = store.getState()
  const userData = state.login.userData

  return function (dispatch) {
    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_UPLOAD_ATTACHMENTS',
        'PROGRESS_STATUS_READ_ATTACHMENTS'
      )
    )

    readFileAttachments(async (err, attachments) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_READ_ATTACHMENTS,
          errorMessage: err.message
        })
      }

      const spToken = await getSpAccessToken(userData)
      const siteUrl = getItemUrl(siteItem)
      const path = fileItem.path

      try {
        for (const attachment of attachments) {
          const { fileData } = attachment
          const fileName = cleanFileName(attachment.fileName)
          console.info('upload: saving attachment', siteUrl, path, fileName)
          dispatch(updateProgressDialog(
            'PROGRESS_STATUS_UPLOAD_ATTACHMENTS',
            fileName
          ))
          await writeSharePointFile({
            siteUrl,
            path,
            fileName,
            fileData
          }, spToken)
        }
      } catch (err) {
        console.error('failed to write attachment to sharepoint', err)
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS,
          errorMessage: err.message
        })
      }

      if (typeof onSaveDone === 'function') {
        onSaveDone()
      }

      try {
        for (const attachment of attachments) {
          const { fileName } = attachment
          const meta = {
            type: siteItem.type,
            id: siteItem.id,
            path,
            fileName,
            url: encodeURI(`${siteUrl}/${path}/${fileName}`)
          }
          await api.addFileSavedEvent(meta)
        }
      } catch (err) {
        console.error('failed to add file save event', err)
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS,
          errorMessage: err.message
        })
      }

      console.info('Successfully uploaded attachments')
      dispatch(endProgressDialog())
    })
  }
}

/**
 * Called from compose mode in outlook.
 *
 * Saves the email -> So the email will get an itemId
 * Reads back the email data (retries until success)
 * Sends the email using the Graph API
 * Files the email in the selected folder
 * Closes the mail window (doesn't work when in inline mode)
 */
export function sendAndFile (siteItem, fileItem) {
  const state = store.getState()
  const userData = state.login.userData

  // TODO we need some sort of progress dialog here
  // since the user doesn't know anything of what is going on

  // TODO make this function async instead of using callbacks
  function readEmailWithRetries (itemId, cb) {
    let retries = config.outlook.readRetryCount
    const retryTimeout = config.outlook.readRetryTimeout

    async function tryReadEmail () {
      let fileData = null

      try {
        const graphToken = await getGraphAccessToken(userData)
        fileData = await readMailFile(itemId, graphToken)
      } catch (err) {
        if (--retries > 0) {
          console.info('Failed to read draft email,', retries, 'retries left')
          setTimeout(tryReadEmail, retryTimeout)
        } else {
          console.error('Failed to read draft email', err)
          cb(err)
        }
        return
      }

      console.info('Read draft email successfully')
      cb(null, fileData)
    }

    setTimeout(tryReadEmail, retryTimeout)
  }

  return async function (dispatch) {
    if (!isOutlookComposeMode()) {
      console.warn('Can only call sendAndFile() in outlook compose mode -> no op')
      return
    }

    if (siteItem.type === 'project') {
      let subject = await getEmailSubjectComposeMode()
      if (!subject.match(`(pds-p${siteItem.id})`)) {
        subject = subject + ` (pds-p${siteItem.id})`
        await setEmailSubjectComposeMode(subject)
      }
    }

    dispatch(beginProgressDialog(
      'PROGRESS_TITLE_SEND_AND_FILE',
      'PROGRESS_STATUS_SAVE_DRAFT_EMAIL'
    ))

    let itemId = null
    try {
      let retries = 5
      do {
        itemId = await saveDraftEmail()
        if (typeof itemId === 'string' && itemId.length > 0) {
          console.info('Got draft mail id', itemId)
          break
        } else if (--retries > 0) {
          console.warn('Retrying save draft email..')
          await wait(2000)
        } else {
          throw new Error('Failed to save draft email')
        }
      } while (true)
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_EMAIL_SAVE_DRAFT,
        errorMessage: err.message
      })
    }

    dispatch(updateProgressDialog(
      'PROGRESS_STATUS_FETCH_EMAIL_WITH_RETRIES'
    ))
    readEmailWithRetries(itemId, async (err, fileData) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_EMAIL_READ,
          errorMessage: err.message
        })
      }

      console.info('Read draft email with length', fileData.length)

      const graphToken = await getGraphAccessToken(userData)
      const fields = await readMailMetadata(itemId, graphToken)

      dispatch(updateProgressDialog(
        'PROGRESS_STATUS_SEND_DRAFT_EMAIL'
      ))

      try {
        await sendMail(itemId, graphToken)
      } catch (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_EMAIL_SEND,
          errorMessage: err.message
        })
      }

      const siteUrl = getItemUrl(siteItem)
      const path = fileItem.path
      const fileName = encodeURI(cleanFileName(await emailFileNameOutlookComposeMode()))
      console.info('upload: saving document on', siteUrl, path, fileName)

      dispatch(updateProgressDialog(
        'PROGRESS_STATUS_UPLOAD_EMAIL'
      ))

      try {
        const spToken = await getSpAccessToken(userData)
        const result = await writeSharePointFile({
          siteUrl,
          path,
          fileName,
          fileData
        }, spToken)
        await updateEmailListItemFields({
          siteUrl,
          listItemUrl: result.listItemUrl,
          fields
        }, spToken)
      } catch (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_UPLOAD,
          errorMessage: err.message
        })
      }

      try {
        const meta = {
          type: siteItem.type,
          id: siteItem.id,
          path,
          fileName,
          url: encodeURI(`${siteUrl}/${path}/${fileName}`)
        }
        await api.addFileSavedEvent(meta)
      } catch (e) {
        console.warn('failed to add file saved event', e)
      }

      console.info('send and file: email saved successfully')
      dispatch(endProgressDialog())
      closeOutlookWindow()
    })
  }
}

/**
 * If host extension is the same as the file extension, do nothing.
 * Otherwise append host extension to current file name.
 */
function computeFileName (fileName) {
  const hostExtension = getFileExtensionFromHost()
  if (hostExtension) {
    const fileExtension = getFileExtension(fileName)
    if (fileExtension === hostExtension) {
      return fileName
    } else {
      return `${fileName}.${hostExtension}`
    }
  } else {
    return fileName
  }
}
